interface ConsoleEntry {
  type: 'log' | 'warn' | 'error';
  args: string[];
  timestamp: number;
}

interface NetworkEntry {
  method: string;
  url: string;
  status: number | null;
  error?: string;
  timestamp: number;
}

interface DebugSnapshot {
  consoleLogs: ConsoleEntry[];
  networkErrors: NetworkEntry[];
  systemInfo: {
    userAgent: string;
    viewport: string;
    screen: string;
    language: string;
    url: string;
    referrer: string;
  };
  timestamp: string;
  uptimeMs: number;
}

const MAX_CONSOLE_ENTRIES = 50;
const MAX_NETWORK_ENTRIES = 10;

class DebugCollector {
  private consoleLogs: ConsoleEntry[] = [];
  private networkErrors: NetworkEntry[] = [];
  private initialized = false;
  private startTime = Date.now();

  private originalConsoleLog: typeof console.log = console.log;
  private originalConsoleWarn: typeof console.warn = console.warn;
  private originalConsoleError: typeof console.error = console.error;
  private originalFetch: typeof window.fetch = window.fetch.bind(window);

  init(): void {
    if (this.initialized) return;
    this.initialized = true;

    this.patchConsole();
    this.patchFetch();
  }

  private patchConsole(): void {
    const self = this;

    console.log = function (...args: unknown[]) {
      self.addConsoleEntry('log', args);
      self.originalConsoleLog.apply(console, args);
    };

    console.warn = function (...args: unknown[]) {
      self.addConsoleEntry('warn', args);
      self.originalConsoleWarn.apply(console, args);
    };

    console.error = function (...args: unknown[]) {
      self.addConsoleEntry('error', args);
      self.originalConsoleError.apply(console, args);
    };
  }

  private addConsoleEntry(type: ConsoleEntry['type'], args: unknown[]): void {
    const serialized = args.map((a) => {
      try {
        if (typeof a === 'string') return a;
        if (a instanceof Error) return `${a.name}: ${a.message}`;
        return JSON.stringify(a)?.substring(0, 500) ?? String(a);
      } catch {
        return String(a);
      }
    });

    this.consoleLogs.push({
      type,
      args: serialized,
      timestamp: Date.now(),
    });

    // Ring-Buffer: aelteste Eintraege entfernen
    if (this.consoleLogs.length > MAX_CONSOLE_ENTRIES) {
      this.consoleLogs = this.consoleLogs.slice(-MAX_CONSOLE_ENTRIES);
    }
  }

  private patchFetch(): void {
    const self = this;
    const origFetch = this.originalFetch;

    window.fetch = async function (
      input: RequestInfo | URL,
      init?: RequestInit,
    ): Promise<Response> {
      const method = init?.method?.toUpperCase() || 'GET';
      const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;

      try {
        const response = await origFetch(input, init);

        if (response.status >= 400) {
          self.addNetworkEntry({ method, url, status: response.status });
        }

        return response;
      } catch (err) {
        self.addNetworkEntry({
          method,
          url,
          status: null,
          error: err instanceof Error ? err.message : String(err),
        });
        throw err;
      }
    };
  }

  private addNetworkEntry(entry: Omit<NetworkEntry, 'timestamp'>): void {
    this.networkErrors.push({ ...entry, timestamp: Date.now() });

    if (this.networkErrors.length > MAX_NETWORK_ENTRIES) {
      this.networkErrors = this.networkErrors.slice(-MAX_NETWORK_ENTRIES);
    }
  }

  getSystemInfo(): DebugSnapshot['systemInfo'] {
    return {
      userAgent: navigator.userAgent,
      viewport: `${window.innerWidth}x${window.innerHeight}`,
      screen: `${screen.width}x${screen.height}`,
      language: navigator.language,
      url: window.location.href,
      referrer: document.referrer || '',
    };
  }

  getSnapshot(): DebugSnapshot {
    return {
      consoleLogs: [...this.consoleLogs],
      networkErrors: [...this.networkErrors],
      systemInfo: this.getSystemInfo(),
      timestamp: new Date().toISOString(),
      uptimeMs: Date.now() - this.startTime,
    };
  }

  clear(): void {
    this.consoleLogs = [];
    this.networkErrors = [];
  }
}

export const debugCollector = new DebugCollector();
